import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, ViewChild, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatMenuTrigger } from '@angular/material/menu';
import { AppConfigService, LocationService, NavigationService } from '@core/services';
import { AppConfigKeys } from '@core/services/app-config/models';
import { FeatureType, FeatureTypes, UserController } from '@core/user';
import { BaseMixinComponent } from '@mixins/components';
import { RefreshableComponentMixin } from '@mixins/components/refreshable';
import { TranslatePipe } from '@ngx-translate/core';
import { ShellController } from '@shell/services';
import { MirIcon } from '@widgets/mir-icon';
import { TooltipDirective } from '@widgets/tooltip';

const BaseTradKey = 'Shell.Sections';

type SectionType =
  'dataSearchRoute'                       // #1
  | 'homeRoute'                           // #2
  | 'businessCatalogRoute'                // #3
  | 'businessCatalogOverviewRoute'        // #3-1:main
  | 'businessCatalogAdministrationRoute'  // #3-2
  | 'hubsRoute'                           // #4
  | 'analyticsHubRoute'                   // #4-1:main
  | 'kpisHubRoute'                        // #4-2
  | 'dataGlossaryRoute'                   // #4-3
  | 'auditOverviewRoute'                  // #5-1:main
  | 'auditRationalisationRoute'           // #5-2
  // separator
  | 'wizardRoute'                         // #6
  | 'wizardBasketsRoute'                  // #6-1:main
  | 'wizardResultsRoute'                  // #6-2
  | 'customIntegratorRoute'               // #7
  | 'adminRoute';                         // #8

type SectionNameToRouteType = { key: string, type: SectionType, feature: FeatureType; };

const SectionNameToRoute: { [key: string]: SectionNameToRouteType; } = {
  Home: { key: 'Home', type: 'homeRoute', feature: null },
  DataSearch: { key: 'DataSearch', type: 'dataSearchRoute', feature: null },
  BusinessCatalog: { key: 'BusinessCatalog', type: 'businessCatalogOverviewRoute', feature: FeatureTypes.ReferentialCompanyBusinessCatalog },
  BCOverview: { key: 'BCOverview', type: 'businessCatalogOverviewRoute', feature: FeatureTypes.ReferentialCompanyBusinessCatalog },
  BCAdmin: { key: 'BCAdmin', type: 'businessCatalogAdministrationRoute', feature: FeatureTypes.ReferentialBusinessCatalogAdministration },
  Hubs: { key: 'Hubs', type: 'analyticsHubRoute', feature: FeatureTypes.ReferentialCompanyHub },
  AnalyticsHub: { key: 'AnalyticsHub', type: 'analyticsHubRoute', feature: FeatureTypes.ReferentialCompanyHub },
  KPIsHub: { key: 'KPIsHub', type: 'kpisHubRoute', feature: FeatureTypes.ReferentialCompanyHub },
  Audit: { key: 'Audit', type: 'auditOverviewRoute', feature: FeatureTypes.ReferentialCompanyAudit },
  AuditOverview: { key: 'AuditOverview', type: 'auditOverviewRoute', feature: FeatureTypes.ReferentialCompanyAudit },
  AuditRationalisation: { key: 'AuditRationalisation', type: 'auditRationalisationRoute', feature: FeatureTypes.ReferentialCompanyAudit },
  DataGlossary: { key: 'DataGlossary', type: 'dataGlossaryRoute', feature: FeatureTypes.ReferentialCompanyHub },
  Wizard: { key: 'Wizard', type: 'wizardRoute', feature: FeatureTypes.ReferentialCompanyWizard },
  WizardBaskets: { key: 'WizardBaskets', type: 'wizardBasketsRoute', feature: FeatureTypes.ReferentialCompanyWizard },
  WizardResults: { key: 'WizardResults', type: 'wizardResultsRoute', feature: FeatureTypes.ReferentialCompanyWizard },
  CustomIntegrator: { key: 'CustomIntegrator', type: 'customIntegratorRoute', feature: FeatureTypes.ReferentialCompanyIntegrator },
  Administration: { key: 'Administration', type: 'adminRoute', feature: FeatureTypes.Administration }
};

const SectionIconName: { [key: string]: string; } = {
  Home: 'home',
  DataSearch: 'manage_search',
  BusinessCatalog: 'menu_book',
  BCOverview: 'token',
  BCAdmin: 'display_settings',
  Hubs: 'hub',
  Audit: 'quick_reference_all',
  AuditOverview: 'assessment',
  AuditRationalisation: 'compress',
  AnalyticsHub: null,
  KPIsHub: null,
  DataGlossary: 'library_books',
  Wizard: 'auto_awesome',
  WizardBaskets: 'auto_awesome',
  WizardResults: 'auto_awesome',
  CustomIntegrator: 'drive_file_move',
  Administration: 'admin_panel_settings'
};

const SectionIconPath: { [key: string]: string; } = {
  AnalyticsHub: './assets/images/icons/ui/analytics-hub-icon.svg',
  KPIsHub: './assets/images/icons/ui/kpis-hub-icon.svg'
};

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'ShellNavigation',
  templateUrl: './shell-navigation.component.html',
  styleUrls: ['./shell-navigation.component.scss'],
  imports: [
    CommonModule,
    TranslatePipe,
    MirIcon,
    TooltipDirective,
  ]
})
export class ShellNavigation extends RefreshableComponentMixin(BaseMixinComponent) {

  @HostBinding('class')
  get hostCssClass(): string {
    return `group/ShellNav`; // for Tailwind logic
  }

  @HostBinding('class.expanded')
  get hostCssExpanded(): boolean { return this.expanded(); }

  @HostBinding('class.local-host')
  get hostCssLocalHost(): boolean { return this.#localHost; }

  /* 🧰 use new API */
  @ViewChild(MatMenuTrigger) referentialsMenuTrigger: MatMenuTrigger;

  /** @Inject */
  readonly #appConfigService = inject(AppConfigService);
  readonly #locationService = inject(LocationService);
  readonly #navigationService = inject(NavigationService);
  readonly #shellController = inject(ShellController);
  readonly #userController = inject(UserController);

  protected readonly expanded = signal(false);
  protected readonly buzinessCatalogSubsections = signal<SectionNameToRouteType[]>([
    SectionNameToRoute['BCOverview'],
    SectionNameToRoute['BCAdmin'],
  ]);
  protected readonly hubsSubsections = signal<SectionNameToRouteType[]>([
    SectionNameToRoute['AnalyticsHub'],
    SectionNameToRoute['KPIsHub'],
    SectionNameToRoute['DataGlossary'],
  ]);
  protected readonly wizardSubsections = signal<SectionNameToRouteType[]>([
    SectionNameToRoute['WizardBaskets'],
    SectionNameToRoute['WizardResults'],
  ]);

  protected readonly auditSubsections = signal<SectionNameToRouteType[]>([
    SectionNameToRoute['AuditOverview'],
    SectionNameToRoute['AuditRationalisation'],
  ]);

  #localHost = false;

  constructor() {
    super();

    this.#localHost = window.location.host.includes('localhost');

    this.#locationService.observe()
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.refresh());

    effect(() => {
      this.expanded.set(this.#shellController.isNavigationExpanded());
    });
  }

  get isRVSUser(): boolean { return this.#userController.isRapidViewsUser; }
  get isDataSearchActive(): boolean { return this.#locationService.pathName.indexOf(this.#appConfigService.get('dataSearchRoute')) >= 0 || this.#locationService.pathName.indexOf(this.#appConfigService.get('wizardRoute')) >= 0; }

  handleToggleExpand() {
    this.#shellController.toggleNavigationExpand();
  }

  handleSelectSection(key: string) {
    const route = this.#appConfigService.get(SectionNameToRoute[key].type as AppConfigKeys);

    if (!SectionNameToRoute[key].feature || this.#shellController.hasFeature(SectionNameToRoute[key].feature)) {
      (this.#navigationService.navigate([route], {}, true) as Promise<boolean>).then(() => {
        (document.activeElement as HTMLElement | null)?.blur();
        this.refresh();
      });
    }
  }

  isCurrentSection(section: string): boolean {
    return this.#isRouteInLocation(section);
  }

  isDisabled(section: string): boolean {
    if (section === 'WizardResults') { return this.#shellController.wizardBasketIsEmpty(); }
    return false;
  }

  hasFeature(feature: FeatureType): boolean {
    return this.#shellController.hasFeature(feature);
  }

  subsectionIconName(section: string): string {
    return this.useIcon(section)
      ? SectionIconName[section]
      : SectionIconPath[section];
  }

  useIcon(section: string): boolean {
    return !!SectionIconName[section];
  }

  subsectionKeyName(subsection: string): string {
    return `${BaseTradKey}.${subsection}`;
  }

  openContactPage() {
    // 🧰 maybe take this url from appConfig?
    window.open('https://www.rapidviews.io/en/contact', '_blank');
  }

  #isRouteInLocation(key: string): boolean {
    const location = this.#locationService.pathName;
    const sectionRoute = this.#appConfigService.get(SectionNameToRoute[key].type as AppConfigKeys);

    if (key === 'Home') { return location === '/home'; }

    if (key === 'BusinessCatalog') {
      return location.includes(sectionRoute)
        || location.includes(this.#appConfigService.get('businessCatalogAdministrationRoute'))
        ;
    }

    if (key === 'Hubs') {
      return location.includes(sectionRoute)
        || location.includes(this.#appConfigService.get('kpisHubRoute'))
        || location.includes(this.#appConfigService.get('dataGlossaryRoute'))
        ;
    }

    if (key === 'Wizard') {
      return location.includes(sectionRoute)
        || location.includes(this.#appConfigService.get('wizardBasketsRoute'))
        || location.includes(this.#appConfigService.get('wizardResultsRoute'))
        ;
    }

    if (key === 'Audit') {
      return location.includes(sectionRoute)
        || location.includes(this.#appConfigService.get('auditRationalisationRoute'))
        ;
    }

    if (key === 'Administration') {
      return location.includes(sectionRoute)
        || location.includes(this.#appConfigService.get('adminCompaniesRoute'))
        || location.includes(this.#appConfigService.get('adminUsersRoute'))
        || location.includes(this.#appConfigService.get('adminAnalyticsRoute'))
        ;
    }

    return location.includes(sectionRoute);
  }
}
